import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, storage } from '../../backend/firebase';
import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  addDoc,
  updateDoc,
  query,
  orderBy,
  getDocs
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import ClipLoader from 'react-spinners/ClipLoader';
import './UpdateDataset.css';

// --- ICONS ---
const FolderIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#667eea" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2v11z"/>
  </svg>
);

const FileIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
    <polyline points="13 2 13 9 20 9"></polyline>
  </svg>
);

const ChevronRight = ({ isOpen }) => (
  <svg 
    width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
  >
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

// --- RECURSIVE TREE NODE COMPONENT ---
const FileTreeNode = ({ node, level, onToggleNode, onRemoveFile }) => {
  const [isOpen, setIsOpen] = useState(level < 1); // Auto-expand root
  const checkboxRef = useRef();

  const isFolder = node.type === 'folder';
  const isChecked = node.checked; 
  const isIndeterminate = node.indeterminate;
  const shouldDim = isFolder ? node.selectedCount === 0 : !isChecked;

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const handleToggle = (e) => {
    e.stopPropagation();
    const newState = !isChecked; 
    onToggleNode(node.path, newState, node.type);
  };

  return (
    <div className="tree-node-container">
      <div 
        className={`tree-node-row ${shouldDim ? 'dimmed' : ''}`} 
        style={{ paddingLeft: `${level * 20}px` }}
        onClick={() => isFolder && setIsOpen(!isOpen)}
      >
        <div className="tree-node-arrow">
          {isFolder && <ChevronRight isOpen={isOpen} />}
        </div>

        <input 
          ref={checkboxRef}
          type="checkbox" 
          checked={isChecked} 
          onChange={handleToggle}
          onClick={(e) => e.stopPropagation()}
          className="tree-checkbox"
        />

        <div className="tree-icon">
          {isFolder ? <FolderIcon /> : <FileIcon />}
        </div>

        <span className="tree-name">
          {node.name}
          {isFolder && <span className="tree-count">({node.selectedCount}/{node.totalFiles})</span>}
          {node.isNew && <span className="tree-badge-new">New</span>}
        </span>

        {!isFolder && (
          <span 
            className="tree-remove-btn" 
            onClick={(e) => { e.stopPropagation(); onRemoveFile(node.id); }}
            title="Delete File"
          >
            ×
          </span>
        )}
      </div>

      {isFolder && isOpen && (
        <div className="tree-children">
          {node.children.map(child => (
            <FileTreeNode 
              key={child.path} 
              node={child} 
              level={level + 1} 
              onToggleNode={onToggleNode}
              onRemoveFile={onRemoveFile}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const UpdateDataset = () => {
  const { datasetid } = useParams();
  const navigation = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Versioning
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [baseVersionId, setBaseVersionId] = useState(null);
  const [versionOptions, setVersionOptions] = useState({ patch: '', minor: '', major: '' });
  const [selectedVersionType, setSelectedVersionType] = useState('minor');
  const [finalVersion, setFinalVersion] = useState('');

  // Files
  const [selectedFiles, setSelectedFiles] = useState([]);
  
  // Inputs for New Files
  const [fileTypeName, setFileTypeName] = useState(''); 
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');

  // --- PATH MANAGEMENT STATE ---
  const [selectedPathOption, setSelectedPathOption] = useState('');
  const [newFolderName, setNewFolderName] = useState('');

  // --- 1. Calculate Locked Root & Existing Paths ---
  const { lockedRoot, existingSubPaths } = useMemo(() => {
    // Only consider files that are meant to be kept for the root lock logic
    const keptFiles = selectedFiles.filter(f => f.keep);
    
    if (keptFiles.length === 0) {
      return { lockedRoot: null, existingSubPaths: [] };
    }

    const firstPath = keptFiles[0].relativePath;
    const rootSegment = firstPath.split('/')[0];
    
    const paths = new Set();
    paths.add(rootSegment + '/');

    keptFiles.forEach(f => {
      const parts = f.relativePath.split('/');
      let current = "";
      for (let i = 0; i < parts.length - 1; i++) {
        current += parts[i] + "/";
        paths.add(current);
      }
    });

    return { 
      lockedRoot: rootSegment, 
      existingSubPaths: Array.from(paths).sort() 
    };
  }, [selectedFiles]);

  // --- 2. Build File Tree Structure (Memoized) ---
  const fileTree = useMemo(() => {
    const tree = [];
    const findNode = (nodes, name) => nodes.find(n => n.name === name && n.type === 'folder');

    // Step A: Build raw tree
    selectedFiles.forEach(file => {
      const parts = file.relativePath.split('/');
      let currentLevel = tree;
      let currentPath = "";

      parts.forEach((part, index) => {
        const isLast = index === parts.length - 1;
        currentPath += part + (isLast ? "" : "/");

        if (isLast) {
          currentLevel.push({
            name: part,
            path: currentPath,
            type: 'file',
            id: file.id,
            checked: file.keep,
            isNew: file.isNew,
            totalFiles: 1,
            selectedCount: file.keep ? 1 : 0
          });
        } else {
          let folderNode = findNode(currentLevel, part);
          if (!folderNode) {
            folderNode = {
              name: part,
              path: currentPath,
              type: 'folder',
              children: [],
              totalFiles: 0,
              selectedCount: 0,
              checked: false,
              indeterminate: false
            };
            currentLevel.push(folderNode);
          }
          currentLevel = folderNode.children;
        }
      });
    });

    // Step B: Recursive Stats Calculation
    const processStats = (nodes) => {
      let totalFiles = 0;
      let selectedCount = 0;
      let allChildrenChecked = true;
      let noChildrenChecked = true;

      nodes.forEach(node => {
        if (node.type === 'folder') {
          const stats = processStats(node.children);
          node.totalFiles = stats.totalFiles;
          node.selectedCount = stats.selectedCount;
          node.checked = stats.allChildrenChecked && stats.totalFiles > 0;
          node.indeterminate = !stats.allChildrenChecked && !stats.noChildrenChecked;
          
          totalFiles += stats.totalFiles;
          selectedCount += stats.selectedCount;
          
          if (!node.checked) allChildrenChecked = false;
          if (stats.selectedCount > 0) noChildrenChecked = false;
        } else {
          totalFiles += 1;
          if (node.checked) {
            selectedCount += 1;
            noChildrenChecked = false;
          } else {
            allChildrenChecked = false;
          }
        }
      });
      
      if (nodes.length === 0) {
        allChildrenChecked = false;
        noChildrenChecked = true;
      }

      return { totalFiles, selectedCount, allChildrenChecked, noChildrenChecked };
    };

    processStats(tree);
    
    const sortNodes = (nodes) => {
      nodes.sort((a, b) => {
        if (a.type === b.type) return a.name.localeCompare(b.name);
        return a.type === 'folder' ? -1 : 1;
      });
      nodes.forEach(n => { if (n.children) sortNodes(n.children); });
    };
    sortNodes(tree);

    return tree;
  }, [selectedFiles]);

  // --- INIT ---
  useEffect(() => {
    fetchDatasetInfo();
  }, [datasetid]);

  useEffect(() => {
    if (versionOptions[selectedVersionType]) {
      setFinalVersion(versionOptions[selectedVersionType]);
    }
  }, [selectedVersionType, versionOptions]);

  // --- FIX: PATH SYNCHRONIZATION ---
  // Ensures dropdown is set correctly when lockedRoot appears
  useEffect(() => {
    if (lockedRoot) {
      if (!selectedPathOption || selectedPathOption === 'NEW_ROOT' || !selectedPathOption.startsWith(lockedRoot)) {
        setSelectedPathOption(lockedRoot + '/');
      }
    } else {
      if (selectedPathOption !== 'NEW_ROOT') {
        setSelectedPathOption('NEW_ROOT');
      }
    }
  }, [lockedRoot, selectedPathOption]); 

  const calculateNextVersions = (verString) => {
    const cleanVer = verString.replace(/[^0-9.]/g, ''); 
    const parts = cleanVer.split('.').map(Number);
    while (parts.length < 3) parts.push(0);
    const [major, minor, patch] = parts;

    return {
      patch: `${major}.${minor}.${patch + 1}`,
      minor: `${major}.${minor + 1}.0`,
      major: `${major + 1}.0.0`
    };
  };

  const fetchDatasetInfo = async () => {
    try {
      const datasetDocRef = doc(db, 'datasets', datasetid);
      const datasetDoc = await getDoc(datasetDocRef);
      
      if (datasetDoc.exists()) {
        const data = datasetDoc.data();
        setDatasetInfo(data);

        const latestVer = data.latestVersion || '0.0.0';
        const nextVers = calculateNextVersions(latestVer);
        setVersionOptions(nextVers);
        setFinalVersion(nextVers.minor); 

        const versionsRef = collection(db, 'datasets', datasetid, 'versions');
        const q = query(versionsRef, orderBy('publishedOn', 'desc'));
        const querySnapshot = await getDocs(q);

        let targetVersionData = null;
        let targetVersionId = null;

        for (const docSnapshot of querySnapshot.docs) {
          const vData = docSnapshot.data();
          if (!vData.isDisabled) {
            targetVersionData = vData;
            targetVersionId = docSnapshot.id;
            break; 
          }
        }

        if (targetVersionData && targetVersionId) {
          setBaseVersionId(targetVersionId);
          const versionFiles = (targetVersionData.files || []).map((f, index) => ({
            id: `prev-${index}-${Date.now()}`, 
            title: f.title,
            fileName: f.fileName,
            relativePath: f.path || f.fileName, 
            fileUrl: f.fileUrl,
            fileFormat: f.fileFormat,
            uploadedAt: f.uploadedAt || data.lastUpdated,
            version: targetVersionId,
            keep: true,
            isNew: false
          }));
          setSelectedFiles(versionFiles);
        }
      }
    } catch (error) {
      console.error('Error fetching dataset info:', error);
      alert('Error loading dataset information');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setFileName(selectedFile ? selectedFile.name : '');
    if (!fileTypeName && selectedFile) setFileTypeName(selectedFile.name);
  };

  // --- DESTINATION PATH LOGIC ---
  const getDestinationPath = () => {
    if (lockedRoot) {
      // 1. Check for Subfolder Creation
      if (selectedPathOption === 'CREATE_SUB') {
        const subName = newFolderName.trim().replace(/[\/\\]/g, ''); 
        if (!subName) return null;
        return `${lockedRoot}/${subName}/`;
      } 
      
      // 2. Check for Valid Existing Selection
      if (selectedPathOption && selectedPathOption.startsWith(lockedRoot)) {
        return selectedPathOption;
      }
      
      // 3. Fallback: If lockedRoot exists but selection is stale/empty, return Root
      return `${lockedRoot}/`;
    } else {
      // 4. New Root Logic (For Single File Uploads)
      const rootName = newFolderName.trim().replace(/[\/\\]/g, '');
      if (!rootName) return null;
      return `${rootName}/`;
    }
  };

  const handleAddFile = () => {
    if (!fileTypeName.trim()) return alert("Please enter a file name");
    if (!file) return alert("Please select a file first");

    const destPath = getDestinationPath();
    if (!destPath) return alert("Please specify a valid folder name/path.");

    const finalRelativePath = destPath + fileName;

    const newFile = {
      id: `new-${Date.now()}`,
      title: fileTypeName,
      file: file,
      fileName: fileName,
      relativePath: finalRelativePath,
      fileFormat: fileName.split('.').pop(),
      isNew: true,
      keep: true
    };

    setSelectedFiles([...selectedFiles, newFile]);
    setFileTypeName('');
    setFile(null);
    setFileName('');
    if(selectedPathOption === 'CREATE_SUB' || !lockedRoot) setNewFolderName(''); 
    
    // Reset file input
    const fileInput = document.getElementById("update-dataset-file-upload");
    if (fileInput) fileInput.value = "";
  };

  // --- UPDATED: AUTOMATIC ROOT DETECTION FOR FOLDER UPLOAD ---
  const handleFolderUpload = (e) => {
    const files = Array.from(e.target.files);
    if(files.length === 0) return;

    let destPath = null;
    let autoDetectedRoot = null;

    // IF STARTING FRESH (No Locked Root): Use folder name as Root
    if (!lockedRoot) {
      // Get the top folder name (e.g. "MyData/img.jpg" -> "MyData")
      const firstFilePath = files[0].webkitRelativePath;
      if (firstFilePath) {
        autoDetectedRoot = firstFilePath.split('/')[0];
        setNewFolderName(autoDetectedRoot); // Set the input visually
      } else {
        return alert("Cannot detect folder structure. Please enter a root name manually.");
      }
    } 
    // IF ADDING TO EXISTING: Use normal logic
    else {
      destPath = getDestinationPath();
      if (!destPath) return alert("Please specify a valid destination path.");
    }

    const newFolderFiles = files.map(f => {
      let fullPath;

      if (autoDetectedRoot) {
        // Use browser's path directly (contains the root name already)
        fullPath = f.webkitRelativePath; 
      } else {
        // Prepend selected path (e.g. "Root/Sub/" + "Folder/file.txt")
        fullPath = destPath + (f.webkitRelativePath || f.name);
      }

      return {
        id: `new-folder-${Date.now()}-${Math.random()}`,
        title: fullPath,
        fileName: f.name,
        relativePath: fullPath,
        file: f,
        fileFormat: f.name.split('.').pop(),
        isNew: true,
        keep: true
      }
    });

    setSelectedFiles(prev => [...prev, ...newFolderFiles]);
    
    // Only clear newFolderName if we were manually creating a subfolder
    if (selectedPathOption === 'CREATE_SUB') {
      setNewFolderName('');
    }
    
    e.target.value = ""; 
  }

  const handleRemoveFile = (fileId) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleToggleNode = (path, newCheckedState, type) => {
    setSelectedFiles(prevFiles => {
      return prevFiles.map(f => {
        if (type === 'folder') {
          const folderPrefix = path.endsWith('/') ? path : path + '/';
          if (f.relativePath.startsWith(folderPrefix) || f.relativePath === path) {
            return { ...f, keep: newCheckedState };
          }
        } 
        else if (f.relativePath === path) {
           return { ...f, keep: newCheckedState };
        }
        return f;
      });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const keptFiles = selectedFiles.filter(f => f.keep);
    if (keptFiles.length > 0) {
        const firstRoot = keptFiles[0].relativePath.split('/')[0];
        const hasMixedRoots = keptFiles.some(f => f.relativePath.split('/')[0] !== firstRoot);
        if (hasMixedRoots) {
            alert(`Error: Multiple root folders detected. Please ensure all files belong to one root folder (e.g. ${firstRoot}/)`);
            return;
        }
    } else {
        alert('Please select at least one file to keep');
        return;
    }

    const versionRef = doc(db, 'datasets', datasetid, 'versions', finalVersion);
    const versionSnap = await getDoc(versionRef);
    if (versionSnap.exists()) {
      alert(`Version ${finalVersion} already exists.`);
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      const finalFilesList = [];

      for (let i = 0; i < keptFiles.length; i++) {
        const fileData = keptFiles[i];
        
        if (fileData.isNew) {
          const fileExt = fileData.fileName.split('.').pop();
          const filePath = `datasets/${datasetid}/${finalVersion}/${fileData.relativePath}`;
          const storageRef = ref(storage, filePath);

          const publicURL = await new Promise((resolve, reject) => {
            const uploadTask = uploadBytesResumable(storageRef, fileData.file);
            uploadTask.on("state_changed",
              (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress.toFixed(0));
              },
              (error) => reject(error),
              async () => resolve(await getDownloadURL(uploadTask.snapshot.ref))
            );
          });

          finalFilesList.push({
            title: fileData.title,
            fileName: fileData.fileName,
            fileUrl: publicURL,
            fileFormat: fileExt,
            uploadedAt: new Date().toISOString(),
            path: fileData.relativePath
          });
        } else {
          finalFilesList.push({
            title: fileData.title,
            fileName: fileData.fileName,
            fileUrl: fileData.fileUrl,
            fileFormat: fileData.fileFormat,
            uploadedAt: fileData.uploadedAt,
            path: fileData.relativePath || fileData.fileName
          });
        }
      }

      for (const f of finalFilesList) {
         await addDoc(collection(db, "datasets", datasetid, "files"), {
           ...f,
           version: finalVersion,
           downloads: 0
         });
      }

      await setDoc(doc(db, "datasets", datasetid, "versions", finalVersion), {
        files: finalFilesList,
        publishedOn: new Date().toISOString(),
        downloads: 0,
        fileCount: finalFilesList.length,
        isDisabled: false,
        type: selectedVersionType 
      });

      await updateDoc(doc(db, "datasets", datasetid), {
        latestVersion: finalVersion,
        lastUpdated: new Date().toISOString(),
        fileCount: finalFilesList.length,
      });

      alert("Dataset updated successfully!");
      navigation(-1);
    } catch (error) {
      console.error("Error:", error.message);
      alert("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="update-dataset-page-container">
      <div className="update-dataset-page-content">
        <div className="update-dataset-form-header">
          <p className="update-dataset-form-subtitle">Update your dataset</p>
          {datasetInfo && (
            <div className="update-dataset-current-info">
              <span>Current: <strong>{datasetInfo.latestVersion}</strong></span>
              <span>Based on: {baseVersionId ? `v${baseVersionId}` : 'None'}</span>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="update-dataset-main-form">
          <div className="update-dataset-form-grid">
            
            {/* --- LEFT: VERSION & LIST --- */}
            <div className="update-dataset-form-column">
              <div className="update-dataset-form-field">
                <span className="update-dataset-form-label">Update Type</span>
                <div className="version-selector-container">
                  {['patch', 'minor', 'major'].map(type => (
                    <label key={type} className={`version-option-card ${selectedVersionType === type ? 'selected' : ''}`}>
                      <input type="radio" value={type} checked={selectedVersionType === type} onChange={() => setSelectedVersionType(type)} className="version-radio-hidden" />
                      <div className="version-card-header">
                        <span className={`version-badge ${type}`}>{type}</span>
                        <span className="version-number">{versionOptions[type]}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="update-dataset-form-field">
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                   <span className="update-dataset-form-label">
                     Files System ({selectedFiles.filter(f => f.keep).length} kept)
                   </span>
                   {selectedFiles.length > 0 && (
                     <button 
                       type="button" 
                       className="tree-text-btn"
                       onClick={() => {
                          const allKept = selectedFiles.every(f => f.keep);
                          setSelectedFiles(prev => prev.map(f => ({...f, keep: !allKept})));
                       }}
                     >
                       {selectedFiles.every(f => f.keep) ? "Uncheck All" : "Check All"}
                     </button>
                   )}
                </div>
                
                {/* --- TREE VIEW CONTAINER --- */}
                <div className="update-dataset-files-list tree-view-wrapper">
                   {fileTree.length > 0 ? (
                      fileTree.map(node => (
                        <FileTreeNode 
                          key={node.path} 
                          node={node} 
                          level={0} 
                          onToggleNode={handleToggleNode}
                          onRemoveFile={handleRemoveFile}
                        />
                      ))
                   ) : (
                     <div className="empty-tree-state">No files added yet.</div>
                   )}
                </div>
              </div>
            </div>

            {/* --- RIGHT: UPLOAD LOGIC --- */}
            <div className="update-dataset-form-column">
              <div className="update-dataset-form-section">
                <h3 className="update-dataset-section-title">Add Files</h3>

                {/* DYNAMIC PATH SELECTOR */}
                <div className="update-dataset-form-field">
                    <span className="update-dataset-form-label" style={{color: '#667eea'}}>
                        Destination Path
                    </span>

                    {lockedRoot ? (
                        <div className="locked-path-control">
                            <span className="locked-root-badge">Root Locked: <b>{lockedRoot}/</b></span>
                            <select 
                                className="update-dataset-form-select"
                                // Fallback: if 'NEW_ROOT' (stale), show the locked root visual fallback
                                value={selectedPathOption === 'NEW_ROOT' ? `${lockedRoot}/` : selectedPathOption}
                                onChange={(e) => setSelectedPathOption(e.target.value)}
                            >
                                <option value="" disabled>Select a path...</option>
                                {existingSubPaths.map(path => (
                                    <option key={path} value={path}>{path}</option>
                                ))}
                                <option value="CREATE_SUB">Create New Subfolder...</option>
                            </select>

                            {selectedPathOption === 'CREATE_SUB' && (
                                <input 
                                    className="update-dataset-form-input mt-2"
                                    placeholder="Enter subfolder name"
                                    value={newFolderName}
                                    onChange={(e) => setNewFolderName(e.target.value)}
                                />
                            )}
                        </div>
                    ) : (
                        <div className="new-root-control">
                            <span className="path-info-text">All previous files removed. Create a new Root folder.</span>
                            <input 
                                className="update-dataset-form-input"
                                placeholder="Enter Root Name (or upload a folder to auto-fill)"
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                            />
                        </div>
                    )}
                </div>

                <div className="separator-line"></div>

                {/* ADD SINGLE FILE */}
                <span className="update-dataset-subsection-label">Add Single File</span>
                <div className="update-dataset-form-field">
                  <div className="update-dataset-file-upload-area small-area">
                    <input type="file" onChange={handleFileChange} className="update-dataset-file-input" id="update-dataset-file-upload" />
                    <label htmlFor="update-dataset-file-upload" className="update-dataset-file-upload-label">
                        <span className="update-dataset-file-upload-text">{fileName ? `Selected: ${fileName}` : "Click to Select File"}</span>
                    </label>
                  </div>
                  <button type="button" onClick={handleAddFile} className="update-dataset-add-file-button" disabled={!file}>
                    Add File
                  </button>
                </div>

                <div className="separator-line"></div>

                {/* ADD FOLDER */}
                <span className="update-dataset-subsection-label">Add Folder</span>
                <div className="update-dataset-form-field">
                    <div className="update-dataset-file-upload-area">
                        <input type="file" webkitdirectory="" directory="" multiple onChange={handleFolderUpload} className="update-dataset-file-input" id="update-dataset-folder-upload" />
                        <label htmlFor="update-dataset-folder-upload" className="update-dataset-file-upload-label folder-style">
                            <div className="update-dataset-file-upload-content">
                                <span className="update-dataset-file-upload-text">Upload Folder</span>
                            </div>
                        </label>
                    </div>
                </div>

              </div>

              {/* SUMMARY */}
              <div className="update-dataset-form-section">
                <h3 className="update-dataset-section-title">Summary: v{finalVersion}</h3>
                <div className="update-dataset-version-summary">
                  <div className="version-summary-item">
                    <span className="summary-label">Target Version:</span>
                    <span className="summary-value" style={{color: '#667eea'}}>{finalVersion}</span>
                  </div>
                  <div className="version-summary-item">
                    <span className="summary-label">Files to Publish:</span>
                    <span className="summary-value">{selectedFiles.filter(f => f.keep).length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="update-dataset-form-actions">
            <button type="button" onClick={() => navigation(-1)} className="update-dataset-cancel-button">Cancel</button>
            <button type="submit" disabled={loading || !finalVersion || selectedFiles.filter(f => f.keep).length === 0} className="update-dataset-submit-button">
              {loading ? <ClipLoader size={16} color="#ffffff" /> : `Publish v${finalVersion}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateDataset;